import moment from 'moment';
import React, {useState} from 'react';

import {Button, Icon, Menu, Text, WithTooltip} from '@albert-io/atomic';
import {history} from 'client/history';

import {useAssignmentReleaseScope} from './hooks/useAssignmentReleaseScope';
import type {ReleaseStudentCounts} from './AssignmentsList.helpers';
import {NO_RELEASABLE_SUBMISSIONS} from './AssignmentsList.helpers';
import ReleaseGradesModal from './ReleaseGradesModal.react';

/** Why the button is dead before anyone has answered — distinct from having nothing releasable. */
const NO_SUBMISSIONS_TO_GRADE = 'No submissions to grade yet';

const formatReleasedAt = (releasedAt: string): string =>
  moment(releasedAt).format('M/D/YY [at] h:mm a');

interface AssignmentGradeMenuButtonProps {
  assignmentId: string;
  assignmentName: string;
  /** Absent on All Classwork: the grading view resolves its own class and a release spans them all. */
  classroomId?: string;
  classroomName?: string;
  /** Finished grades this class's students cannot see yet — what a release would send. */
  unreleasedCompletedCount: number;
  /** Who a release would reach in this class; the modal's summary. */
  studentCounts: ReleaseStudentCounts;
  /** False keeps the button on the row, disabled, until the first submission lands. */
  hasSubmissions?: boolean;
  /** Every class in scope has released, so there is nothing left to release. */
  isReleased?: boolean;
  /** When that release went out; `null` until one has. */
  releasedAt?: string | null;
  /** A class in scope withholds results until a due date still ahead; the modal says so (#21337). */
  resultsHiddenUntilDueDate?: boolean;
  /** Set when masquerading, so the scope query reads the same educator's index as the list. */
  asEducatorId?: string;
}

/**
 * One "Grade" menu button whose copy never changes; the row's own label carries grading state.
 *
 * Sits in the row's action cluster, which already stops row-click propagation. Without a
 * `classroomId` (All Classwork) every action spans every class the teacher teaches, which is why a
 * partly-released assignment is not `isReleased`.
 */
export const AssignmentGradeMenuButton = ({
  assignmentId,
  assignmentName,
  classroomId,
  classroomName,
  unreleasedCompletedCount,
  studentCounts,
  hasSubmissions = true,
  isReleased = false,
  releasedAt = null,
  resultsHiddenUntilDueDate = false,
  asEducatorId
}: AssignmentGradeMenuButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    allClassesStudentCounts,
    classroomNames,
    spansMultipleClasses,
    isScopeLoading,
    hasScopeError
  } = useAssignmentReleaseScope({
    assignmentId,
    asEducatorId,
    skip: !isModalOpen
  });

  const canRelease = unreleasedCompletedCount > 0;

  const goToGrading = () =>
    history.pushState(
      null,
      classroomId
        ? `/classes/${classroomId}/assignments/${assignmentId}/student-detail`
        : `/assignments/${assignmentId}/student-detail`
    );
  const openModal = () => setIsModalOpen(true);

  if (!hasSubmissions) {
    return (
      <WithTooltip content={NO_SUBMISSIONS_TO_GRADE} display='flex' placement='bottom'>
        <Button
          size='s'
          color='primary'
          variant='faded'
          disabled
          data-testid={`grade-${assignmentId}`}
        >
          <Text color='inherit'>Grade</Text>
          {/* A disabled button takes no focus, so the tooltip is unreachable without a pointer.
              This carries the same sentence into the accessible name instead. */}
          <span className='sr-only'>{` — ${NO_SUBMISSIONS_TO_GRADE}`}</span>
          <Icon className='ml-x1' icon='caret-down' iconStyle='solid' />
        </Button>
      </WithTooltip>
    );
  }

  return (
    <>
      <Menu closeOnItemClick position='bottom-end' positionFixed>
        <Menu.Trigger>
          {(triggerProps) => (
            <Button
              {...triggerProps}
              size='s'
              color='primary'
              variant='faded'
              data-testid={`grade-${assignmentId}`}
            >
              <Text color='inherit'>Grade</Text>
              <Icon className='ml-x1' icon='caret-down' iconStyle='solid' />
            </Button>
          )}
        </Menu.Trigger>
        {/* Only the inner menu is sized; a narrowed wrapper would let it overflow rightward. */}
        <Menu.Content className='[&>.o-menu-content]:w-[240px] [&>.o-menu-content]:min-w-[240px]'>
          <Menu.Item onClick={goToGrading}>
            {isReleased ? 'Edit Grades' : 'Grade Free Response'}
          </Menu.Item>
          {isReleased ? (
            // `readonly` not `disabled`: the latter fades the row to 0.32 opacity, dropping this
            // sublabel to roughly 1.5:1 against the tray.
            <Menu.Item
              readonly
              disableHover
              textProps={{color: 'tertiary'}}
              subLabel={releasedAt ? `Last published ${formatReleasedAt(releasedAt)}` : undefined}
            >
              Grades Released
            </Menu.Item>
          ) : (
            // `subLabel` not a tooltip: the item is tabIndex={-1} and arrow-key nav skips it, so a
            // hover tooltip would never reach a keyboard or screen-reader user.
            <Menu.Item
              textProps={canRelease ? undefined : {color: 'tertiary'}}
              // `readonly` sets pointer-events: none, which would swallow the cursor too.
              className={canRelease ? undefined : 'pointer-events-auto! cursor-not-allowed!'}
              aria-disabled={canRelease ? undefined : true}
              disableHover={!canRelease}
              readonly={!canRelease}
              onClick={canRelease ? openModal : undefined}
              subLabel={canRelease ? undefined : NO_RELEASABLE_SUBMISSIONS}
            >
              Release Grades
            </Menu.Item>
          )}
        </Menu.Content>
      </Menu>
      {isModalOpen ? (
        <ReleaseGradesModal
          assignmentId={assignmentId}
          assignmentName={assignmentName}
          classroomId={classroomId}
          classroomName={classroomName}
          studentCounts={studentCounts}
          spansMultipleClasses={spansMultipleClasses}
          classroomNames={classroomNames}
          allClassesStudentCounts={allClassesStudentCounts}
          isScopeLoading={isScopeLoading}
          hasScopeError={hasScopeError}
          resultsHiddenUntilDueDate={resultsHiddenUntilDueDate}
          onClose={() => setIsModalOpen(false)}
        />
      ) : null}
    </>
  );
};

export default AssignmentGradeMenuButton;
